import { useCallback, useEffect, useRef, useState } from "react";
import { Archive, ArchiveRestore, Download, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONSENT_LABELS, STAGE_COLORS, STAGE_LABELS, TASK_STATUS, MODULES } from "./config";
import ConsentPanel from "./ConsentPanel";
import MoonRequestsPanel from "./MoonRequestsPanel";

const fmtDate = (v) => (v ? new Date(`${v}T12:00:00`).toLocaleDateString("fr-CA") : "—");
const fmtDateTime = (v) => (v ? new Date(v).toLocaleDateString("fr-CA") : "—");
const fmtMoney = (v) => (v != null && v !== "" ? `${Number(v).toLocaleString("fr-CA")} $` : "—");
const today = () => new Date().toISOString().slice(0, 10);

function StageSelect({ value, options, onChange, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={`rounded-full border-0 px-3 py-1.5 text-[11px] font-bold ${STAGE_COLORS[value] || "bg-slate-100 text-slate-700"}`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function FormModal({ cfg, doc, companies, contacts, onClose, onSaved }) {
  const isEdit = Boolean(doc?.id);
  const [values, setValues] = useState(() => {
    const init = {};
    cfg.formFields.forEach((f) => { init[f.key] = doc?.[f.key] ?? (f.default ?? ""); });
    return init;
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...values };
      if (payload.amount === "") payload.amount = null;
      if (payload.amount != null) payload.amount = Number(payload.amount);
      if (isEdit) await api.put(`${cfg.endpoint}/${doc.id}`, payload);
      else await api.post(cfg.endpoint, payload);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy-950/40 p-4 backdrop-blur-sm" data-testid="crm-form-modal">
      <form onSubmit={submit} className="my-8 w-full max-w-2xl rounded-2xl bg-white p-8 shadow-card">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-navy-950" data-testid="crm-form-title">
            {isEdit ? `Modifier — ${cfg.title}` : `Nouvelle ${cfg.singular}`}
          </h2>
          <button type="button" onClick={onClose} data-testid="crm-form-close" aria-label="Fermer" className="rounded-lg p-2 text-steel hover:text-navy-800"><X size={18} /></button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {cfg.formFields.map((f) => {
            const id = `crm-f-${f.key}`;
            const common = `w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-navy-950 outline-none focus:border-electric`;
            const onChange = (v) => setValues((s) => ({ ...s, [f.key]: v }));
            return (
              <div key={f.key} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <label htmlFor={id} className="mb-1.5 block text-[13px] font-bold text-navy-900">
                  {f.label} {f.required && <span className="text-red-500">*</span>}
                </label>
                {f.type === "textarea" ? (
                  <textarea id={id} data-testid={`crm-field-${f.key}`} rows={3} value={values[f.key] || ""} onChange={(e) => onChange(e.target.value)} className={common} />
                ) : f.type === "select" ? (
                  <select id={id} data-testid={`crm-field-${f.key}`} value={values[f.key] || ""} onChange={(e) => onChange(e.target.value)} className={common}>
                    <option value="">—</option>
                    {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : f.type === "entity" ? (
                  <select id={id} data-testid={`crm-field-${f.key}`} value={values[f.key] || ""} onChange={(e) => onChange(e.target.value)} className={common}>
                    <option value="">—</option>
                    {(f.key === "contact_id" ? contacts : companies).map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.company_name || c.title}</option>
                    ))}
                  </select>
                ) : (
                  <input id={id} data-testid={`crm-field-${f.key}`} type={f.type || "text"} required={f.required}
                    value={values[f.key] || ""} onChange={(e) => onChange(e.target.value)} className={common} />
                )}
              </div>
            );
          })}
        </div>
        {error && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="crm-form-error">{error}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} data-testid="crm-form-cancel" className="rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-graphite hover:text-navy-800">Annuler</button>
          <button type="submit" disabled={saving} data-testid="crm-form-submit"
            className="rounded-full bg-electric px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-electric-dark disabled:opacity-60">
            {saving ? "…" : isEdit ? "Enregistrer" : "Créer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DetailPanel({ cfg, doc, docId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState({ type: "note", content: "" });
  const [task, setTask] = useState({ title: "", due_date: "" });
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api.get(`/crm/activities?${cfg.entityParam}=${docId}`).then(({ data: d }) => setData({ activities: d })).catch(() => setData({ activities: [] }));
  }, [cfg.entityParam, docId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/crm/activities", { ...note, [cfg.entityParam]: docId });
      setNote({ type: "note", content: "" });
      load();
      onChanged();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addTask = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/crm/tasks", { ...task, [cfg.entityParam]: docId, status: "a_faire" });
      setTask({ title: "", due_date: "" });
      onChanged();
      onClose();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-navy-950/40 backdrop-blur-sm" data-testid="crm-detail-panel">
      <div className="h-full w-full max-w-lg overflow-y-auto bg-white p-8 shadow-card">
        <div className="flex items-start justify-between">
          <h2 className="font-display text-xl font-bold text-navy-950" data-testid="crm-detail-title">Historique — {cfg.title}</h2>
          <button onClick={onClose} data-testid="crm-detail-close" aria-label="Fermer" className="rounded-lg p-2 text-steel hover:text-navy-800"><X size={18} /></button>
        </div>

        {cfg.entityParam === "contact_id" && <div className="mt-5"><ConsentPanel contact={doc} onChanged={() => { load(); onChanged(); }} /></div>}
        {cfg.entityParam === "lead_id" && <div className="mt-5"><MoonRequestsPanel lead={doc} onChanged={() => { load(); onChanged(); }} /></div>}

        <form onSubmit={addNote} className="mt-6 rounded-2xl border border-line bg-paper p-4" data-testid="crm-add-note-form">
          <div className="flex gap-2">
            <select value={note.type} onChange={(e) => setNote((s) => ({ ...s, type: e.target.value }))} data-testid="crm-note-type"
              className="rounded-xl border border-line bg-white px-3 py-2 text-sm">
              {[["note", "Note"], ["appel", "Appel"], ["courriel", "Courriel"], ["rencontre", "Rencontre"], ["autre", "Autre"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input required value={note.content} onChange={(e) => setNote((s) => ({ ...s, content: e.target.value }))}
              placeholder="Ajouter une interaction (note, appel, courriel…)…" data-testid="crm-note-content"
              className="flex-1 rounded-xl border border-line bg-white px-4 py-2 text-sm outline-none focus:border-electric" />
            <button type="submit" data-testid="crm-note-submit" className="rounded-xl bg-electric px-4 py-2 text-sm font-bold text-white hover:bg-electric-dark">OK</button>
          </div>
        </form>

        <details className="mt-3 rounded-2xl border border-line bg-paper p-4">
          <summary className="cursor-pointer text-xs font-semibold text-steel hover:text-electric">+ Créer une tâche de suivi</summary>
          <form onSubmit={addTask} className="mt-3 flex gap-2">
            <input required value={task.title} onChange={(e) => setTask((s) => ({ ...s, title: e.target.value }))} placeholder="Titre de la tâche" data-testid="crm-task-title"
              className="flex-1 rounded-xl border border-line bg-white px-4 py-2 text-sm outline-none focus:border-electric" />
            <input type="date" required value={task.due_date} onChange={(e) => setTask((s) => ({ ...s, due_date: e.target.value }))} data-testid="crm-task-due"
              className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-electric" />
            <button type="submit" data-testid="crm-task-submit" className="rounded-xl bg-navy-800 px-4 py-2 text-sm font-bold text-white hover:bg-navy-900">Ajouter</button>
          </form>
        </details>

        {error && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="crm-detail-error">{error}</p>}

        <ol className="relative mt-6 space-y-4 border-l border-line pl-6" data-testid="crm-detail-timeline">
          {(data?.activities || []).map((a) => (
            <li key={a.id} data-testid={`crm-detail-activity-${a.id}`} className="relative">
              <span className={`absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white ${a.type === "appel" ? "bg-emerald-500" : a.type === "courriel" ? "bg-amber-500" : "bg-electric"}`} />
              <p className="text-sm text-graphite">{a.content}</p>
              <p className="mt-0.5 text-xs text-steel">{new Date(a.created_at).toLocaleString("fr-CA")} · {a.type}{a.created_by ? ` · ${a.created_by}` : ""}</p>
            </li>
          ))}
          {data?.activities?.length === 0 && <li className="text-sm text-steel">Aucune interaction enregistrée pour le moment.</li>}
        </ol>
      </div>
    </div>
  );
}

export default function DataModule({ moduleKey }) {
  const cfg = MODULES[moduleKey];
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null); // false | "new" | doc
  const [detailDoc, setDetailDoc] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const fileRef = useRef(null);
  const first = useRef(true);

  const load = useCallback(() => {
    const params = { archived: showArchived ? "all" : "false" };
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setRows(null);
    api.get(cfg.endpoint, { params })
      .then(({ data }) => { setRows(data); setError(""); })
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail)); setRows([]); });
  }, [cfg.endpoint, q, filters, showArchived]);

  useEffect(() => { const t = setTimeout(load, first.current ? 0 : 300); first.current = false; return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (cfg.needsCompanies) api.get("/crm/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    if (cfg.needsContacts) api.get("/crm/contacts").then(({ data }) => setContacts(data)).catch(() => {});
  }, [cfg.needsCompanies, cfg.needsContacts, modal, detailDoc]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 8000); };

  const toggleArchive = async (doc) => {
    setError("");
    try {
      await api.put(`${cfg.endpoint}/${doc.id}`, { archived: !doc.archived });
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Supprimer définitivement « ${doc.name || doc.company_name || doc.title} » ?`)) return;
    try { await api.delete(`${cfg.endpoint}/${doc.id}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeStage = async (doc, field, value) => {
    setError("");
    try {
      await api.put(`${cfg.endpoint}/${doc.id}`, { [field]: value });
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    if (moduleKey === "leads") {
      const fdPreview = new FormData();
      fdPreview.append("file", file);
      try {
        const { data } = await api.post("/crm/import/leads/preview", fdPreview);
        setImportPreview({ report: data, file });
      } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/crm/import/${moduleKey}`, fd);
      flash(`Import terminé : ${data.created} créé(s), ${data.duplicates} doublon(s) ignoré(s), ${data.errors.length} erreur(s) sur ${data.total_rows} ligne(s).`
        + (data.errors.length ? ` Détails : ${data.errors.slice(0, 3).join(" | ")}` : ""));
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const confirmImport = async () => {
    const { file } = importPreview;
    const fd = new FormData();
    fd.append("file", file);
    setError("");
    try {
      const { data } = await api.post(`/crm/import/${moduleKey}`, fd);
      setImportPreview(null);
      flash(`Import terminé : ${data.created} créé(s), ${data.duplicates} doublon(s) ignoré(s), ${data.errors.length} erreur(s) sur ${data.total_rows} ligne(s).`
        + (data.errors.length ? ` Détails : ${data.errors.slice(0, 3).join(" | ")}` : ""));
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    setError("");
    try {
      const res = await api.get(`/crm/export/${moduleKey}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crm_${moduleKey}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      flash("Export CSV téléchargé.");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid={`crm-module-${moduleKey}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-navy-950">{cfg.title}</h1>
          <p className="mt-1 text-sm text-steel">Création, modification, recherche, archivage, import/export CSV.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {cfg.importable && (
            <>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid={`crm-import-input-${moduleKey}`} />
              <button onClick={() => fileRef.current?.click()} data-testid={`crm-import-button-${moduleKey}`}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-[13px] font-semibold text-graphite transition-colors hover:text-electric">
                <Upload size={15} /> Importer CSV
              </button>
              <button onClick={exportCsv} data-testid={`crm-export-button-${moduleKey}`}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-[13px] font-semibold text-graphite transition-colors hover:text-electric">
                <Download size={15} /> Exporter CSV
              </button>
            </>
          )}
          <button onClick={() => setModal("new")} data-testid={`crm-new-button-${moduleKey}`}
            className="inline-flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-electric-dark">
            <Plus size={15} /> {cfg.newLabel || `Nouvelle ${cfg.singular}`}
          </button>
        </div>
      </div>

      {notice && <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid={`crm-notice-${moduleKey}`}>{notice}</p>}
      {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid={`crm-error-${moduleKey}`}>{error}</p>}

      <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-line bg-white p-5 shadow-card">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor={`crm-q-${moduleKey}`} className="mb-1.5 block text-[13px] font-bold text-navy-900">Recherche</label>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-steel" />
            <input id={`crm-q-${moduleKey}`} data-testid={`crm-search-${moduleKey}`} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={cfg.searchPlaceholder} className="w-full rounded-xl border border-line bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-electric" />
          </div>
        </div>
        {(cfg.filters || []).map((f) => (
          <div key={f.key}>
            <label htmlFor={`crm-f-${moduleKey}-${f.key}`} className="mb-1.5 block text-[13px] font-bold text-navy-900">{f.label}</label>
            {f.free ? (
              <input id={`crm-f-${moduleKey}-${f.key}`} data-testid={`crm-filter-${moduleKey}-${f.key}`} value={filters[f.key] || ""}
                onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                className="rounded-xl border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-electric" />
            ) : (
              <select id={`crm-f-${moduleKey}-${f.key}`} data-testid={`crm-filter-${moduleKey}-${f.key}`} value={filters[f.key] || ""}
                onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                className="rounded-xl border border-line bg-white px-4 py-2.5 text-sm">
                <option value="">Tous</option>
                {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
          </div>
        ))}
        {cfg.archivable && (
          <label className="flex cursor-pointer items-center gap-2 pb-2 text-[13px] font-semibold text-graphite">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid={`crm-show-archived-${moduleKey}`} className="h-4 w-4 accent-sky-600" />
            Afficher les archivés
          </label>
        )}
      </div>

      {rows === null ? (
        <p className="text-sm text-steel">Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-steel" data-testid={`crm-empty-${moduleKey}`}>
          Aucun élément pour le moment. Cliquez sur « {cfg.newLabel || `Nouvelle ${cfg.singular}`} » pour commencer.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid={`crm-table-${moduleKey}`}>
            <thead className="bg-paper text-xs uppercase tracking-wider text-steel">
              <tr>{cfg.columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}<th className="px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((doc) => (
                <tr key={doc.id} data-testid={`crm-row-${moduleKey}-${doc.id}`}
                  className={`cursor-pointer border-t border-line transition-colors hover:bg-paper/70 ${doc.archived ? "opacity-50" : ""}`}
                  onClick={() => setDetailDoc(doc)}>
                  {cfg.columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 ${c.bold ? "font-semibold text-navy-800" : "text-graphite"}`}>
                      {c.stage ? (
                        <span onClick={(e) => e.stopPropagation()}>
                          <StageSelect value={doc[c.key]} options={c.key === "status" && moduleKey === "tasks" ? TASK_STATUS : Object.entries(STAGE_LABELS)}
                            onChange={(v) => changeStage(doc, c.key, v)} testid={`crm-stage-${moduleKey}-${doc.id}`} />
                        </span>
                      ) : c.consent ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${doc[c.key] === "desabonne" ? "bg-red-50 text-red-700" : doc.consent_allowed ? "bg-emerald-50 text-emerald-700" : doc[c.key] ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                          {CONSENT_LABELS[doc[c.key]] || "Non établi"}
                        </span>
                      ) : c.badge ? (
                        <span className="rounded-full bg-badge px-2.5 py-0.5 text-[11px] font-bold text-sky-700">
                          {(c.badge.find(([v]) => v === doc[c.key]) || [null, doc[c.key] || "—"])[1]}
                        </span>
                      ) : c.date ? fmtDate(doc[c.key]) : c.datetime ? fmtDateTime(doc[c.key]) : c.money ? fmtMoney(doc[c.key]) :
                        c.link === "mailto" ? <a href={`mailto:${doc[c.key]}`} onClick={(e) => e.stopPropagation()} className="text-electric underline">{doc[c.key] || "—"}</a> :
                        c.link === "tel" ? <a href={`tel:${doc[c.key]}`} onClick={(e) => e.stopPropagation()} className="text-electric underline">{doc[c.key] || "—"}</a> :
                        (doc[c.key] || "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => setModal(doc)} data-testid={`crm-edit-${moduleKey}-${doc.id}`} aria-label="Modifier" className="rounded-lg p-2 text-steel transition-colors hover:text-electric"><Pencil size={15} /></button>
                      {cfg.archivable && (
                        <button onClick={() => toggleArchive(doc)} data-testid={`crm-archive-${moduleKey}-${doc.id}`} aria-label={doc.archived ? "Désarchiver" : "Archiver"} className="rounded-lg p-2 text-steel transition-colors hover:text-amber-600">
                          {doc.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                        </button>
                      )}
                      <button onClick={() => remove(doc)} data-testid={`crm-delete-${moduleKey}-${doc.id}`} aria-label="Supprimer" className="rounded-lg p-2 text-steel transition-colors hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <FormModal cfg={cfg} doc={modal === "new" ? null : modal} companies={companies} contacts={contacts}
        onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {detailDoc && <DetailPanel cfg={cfg} doc={detailDoc} docId={detailDoc.id} onClose={() => setDetailDoc(null)} onChanged={load} />}

      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy-950/50 p-4 backdrop-blur-sm" data-testid="crm-import-preview-modal">
          <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-8 shadow-card">
            <h2 className="font-display text-xl font-bold text-navy-950">Aperçu de l'import — {cfg.title}</h2>
            <p className="mt-1 text-sm text-steel">Rien n'est encore importé. Vérifiez puis confirmez.</p>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-emerald-50 p-3"><p className="font-display text-2xl font-bold text-emerald-700">{importPreview.report.would_create}</p><p className="text-xs font-semibold text-emerald-700">à créer</p></div>
              <div className="rounded-xl bg-amber-50 p-3"><p className="font-display text-2xl font-bold text-amber-700">{importPreview.report.duplicates}</p><p className="text-xs font-semibold text-amber-700">doublon(s) ignoré(s)</p></div>
              <div className="rounded-xl bg-slate-100 p-3"><p className="font-display text-2xl font-bold text-slate-700">{importPreview.report.total_rows}</p><p className="text-xs font-semibold text-slate-700">lignes au total</p></div>
            </div>
            <div className="mt-4 text-xs text-graphite">
              <p><span className="font-bold text-navy-900">Colonnes reconnues :</span> {importPreview.report.columns_recognized.join(", ") || "aucune"}</p>
              {importPreview.report.columns_ignored.length > 0 && (
                <p className="mt-1"><span className="font-bold text-navy-900">Colonnes ignorées :</span> {importPreview.report.columns_ignored.join(", ")}</p>
              )}
              {importPreview.report.errors.length > 0 && (
                <p className="mt-1 text-red-600">{importPreview.report.errors.join(" | ")}</p>
              )}
            </div>
            {importPreview.report.sample.length > 0 && (
              <div className="mt-4 overflow-x-auto rounded-xl border border-line">
                <table className="w-full min-w-[600px] text-left text-xs" data-testid="crm-import-preview-rows">
                  <thead className="bg-paper text-[10px] uppercase tracking-wider text-steel">
                    <tr><th className="px-3 py-2">Entreprise</th><th className="px-3 py-2">Contact</th><th className="px-3 py-2">Courriel</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Vérifié le</th><th className="px-3 py-2">Consentement</th></tr>
                  </thead>
                  <tbody>
                    {importPreview.report.sample.map((s, i) => (
                      <tr key={i} className="border-t border-line">
                        <td className="px-3 py-2 font-semibold text-navy-800">{s.company_name}</td>
                        <td className="px-3 py-2">{s.contact_name || "—"}</td>
                        <td className="px-3 py-2">{s.email || "—"}</td>
                        <td className="px-3 py-2">{s.source || "—"}</td>
                        <td className="px-3 py-2">{s.verified_at || "—"}</td>
                        <td className="px-3 py-2">{s.consent_status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setImportPreview(null)} data-testid="crm-import-preview-cancel" className="rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-graphite hover:text-navy-800">Annuler</button>
              <button onClick={confirmImport} disabled={importPreview.report.would_create === 0} data-testid="crm-import-preview-confirm"
                className="rounded-full bg-electric px-6 py-2.5 text-sm font-bold text-white hover:bg-electric-dark disabled:opacity-50">
                Confirmer l'import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
